import { SERVER_ERROR_MESSAGE } from "./apiError";
import { resolveApiBaseUrl } from "./apiBase";

export interface UploadOptions {
    collection?: string;
    table?: string;
    uid?: string;
    field?: string;
    userId?: string;
    /** When true, file is stored temporarily (temp/); no files collection record. Finalize on save. */
    temporary?: boolean;
    /** Initial Labwick sharing scope applied at creation (default 'private'). */
    visibility?: FileVisibility;
    /** ACL group uid when visibility is 'shared' (legacy single-group form). */
    group?: string | null;
    /** ACL group uids when visibility is 'shared' (multi-group form). */
    groups?: string[];
    /** Serve-time public gate (default true). */
    public?: boolean;
}

export interface FileLinkParams {
    collection: string;
    itemUid: string;
    field: string;
    filename: string;
}

export type RemoveLinkParams = FileLinkParams;

export type FileVisibility = 'private' | 'shared';

export interface FileSharing {
    visibility: FileVisibility;
    /** Legacy single-group form; kept in sync with the first entry of `groups`. */
    group: string | null;
    /** ACL group uids a 'shared' file belongs to. */
    groups: string[];
    public: boolean;
}

interface UploadResponse {
    success: boolean;
    filename?: string;
    fileId?: string;
    message?: string;
    file?: { name: string; original: string; size: number; type: string; url: string };
}

function getAuthToken(): string | null {
    return localStorage.getItem('idToken') || localStorage.getItem('authToken');
}

/** Configured max upload size in bytes (mirrors backend MAX_FILE_SIZE). */
function resolveMaxUploadBytes(): number {
    const mb = Number(import.meta.env.VITE_MAX_FILE_SIZE_MB);
    if (Number.isFinite(mb) && mb > 0) return Math.round(mb * 1024 * 1024);
    const bytes = Number(import.meta.env.VITE_MAX_FILE_SIZE);
    if (Number.isFinite(bytes) && bytes > 0) return bytes;
    return 10 * 1024 * 1024;
}

const MAX_UPLOAD_BYTES = resolveMaxUploadBytes();
const MAX_UPLOAD_MB = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));

/**
 * Parse a fetch Response as JSON without throwing when the body is empty or
 * non-JSON (e.g. a proxy 413/HTML error page). Returns null on failure.
 */
async function parseJsonSafe<T = any>(response: Response): Promise<T | null> {
    const text = await response.text().catch(() => '');
    if (!text) return null;
    try {
        return JSON.parse(text) as T;
    } catch {
        return null;
    }
}

/** Friendly message for a too-large upload (413), regardless of body content. */
const TOO_LARGE_MESSAGE = `This file is too large to upload (max ${MAX_UPLOAD_MB} MB).`;

/**
 * Read the current user's remembered file-sharing defaults from localStorage,
 * so uploads are created with the user's preference even before the prompt resolves.
 */
export function readSharingDefaults(): Pick<UploadOptions, 'visibility' | 'group' | 'public'> {
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        if (!user) return {};
        const out: Pick<UploadOptions, 'visibility' | 'group' | 'public'> = {};
        if (user.fileVisibilityDefault === 'shared' || user.fileVisibilityDefault === 'private') {
            out.visibility = user.fileVisibilityDefault;
        }
        if (typeof user.fileShareGroupDefault === 'string') out.group = user.fileShareGroupDefault;
        if (typeof user.filePublicDefault === 'boolean') out.public = user.filePublicDefault;
        return out;
    } catch {
        return {};
    }
}

export function getApiBaseUrl(): string {
    return resolveApiBaseUrl();
}

const uploadFile = async (file: File, options?: UploadOptions): Promise<UploadResponse | null> => {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';

        if (!api_path) {
            console.error('API path is missing.');
            return null;
        }
        if (!token && !key) {
            console.error('No authentication token or key available.');
            return null;
        }

        const formData = new FormData();
        formData.append('file', file);
        if (options?.collection) formData.append('collection', options.collection);
        if (options?.table) formData.append('table', options.table);
        const itemUid = options?.uid;
        if (itemUid) formData.append('uid', itemUid);
        if (options?.field) formData.append('field', options.field);
        if (options?.userId) formData.append('userId', options.userId);
        if (options?.visibility) formData.append('visibility', options.visibility);
        if (options?.groups?.length) {
            for (const g of options.groups) formData.append('groups', g);
        } else if (options?.group) {
            formData.append('group', options.group);
        }
        if (options?.public !== undefined) formData.append('public', String(options.public));

        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        } else if (key) {
            headers['key'] = key;
        }

        const queryParams = new URLSearchParams();
        if (options?.collection) queryParams.set('collection', options.collection);
        if (options?.temporary) queryParams.set('temporary', '1');
        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
        const response: any = await fetch(`${api_path}/file/upload${query}`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }

        // 413 bodies are often non-JSON (proxy/HTML), so never call response.json() blindly.
        if (response.status === 413) {
            console.error('Failed to upload file: 413 Payload Too Large');
            return { success: false, message: TOO_LARGE_MESSAGE };
        }

        const data = await parseJsonSafe<UploadResponse>(response);

        if (response.status !== 200) {
            console.error(`Failed to upload file: ${response.status} - ${response.statusText}`);
            if (response.status >= 500) {
                return {
                    success: false,
                    message: SERVER_ERROR_MESSAGE
                };
            }
            return data || { success: false, message: `Upload failed (HTTP ${response.status}).` };
        }

        if (!data) {
            console.error('API response indicates failure: empty or non-JSON body');
            return { success: false, message: 'Unexpected response from server.' };
        }

        return data;

    } catch (error) {
        console.error('An error occurred while uploading the file:', error);
        return null;
    }
};

/**
 * Upload an image to the REMOTE server backing a connector collection
 * (e.g. veeninglab). Returns the stored filename to write into the picture field.
 */
export async function uploadConnectorImage(
    collection: string,
    file: File
): Promise<{ success: boolean; filename?: string; registered?: boolean; message?: string } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        if (!api_path || !token || !collection) return null;

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(
            `${api_path}/collections/${encodeURIComponent(collection)}/upload-image`,
            {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            }
        );

        const data = await response.json().catch(() => null);
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        if (!response.ok) {
            return {
                success: false,
                message: data?.message || (response.status >= 500 ? SERVER_ERROR_MESSAGE : `Upload failed (${response.status})`)
            };
        }
        return data;
    } catch (error) {
        console.error('Connector image upload error:', error);
        return null;
    }
}

export interface FileRecord {
    uid: string;
    name: string;
    original: string;
    size: number;
    type: string;
    user: string | null;
    visibility?: FileVisibility;
    group?: string | null;
    groups?: string[];
    public?: boolean;
    relations: Array<{ table: string; uid: string; field: string; linkedAt?: string }>;
    /** When the file was uploaded (Mongoose document creation timestamp). */
    createdAt?: string;
    _updated?: string;
}

/** Update a file's Labwick sharing scope and public serve gate (owner or admin only). */
export async function updateFileVisibility(
    fileId: string,
    sharing: Partial<FileSharing>
): Promise<{ success: boolean; file?: FileRecord; message?: string } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';
        if (!api_path || (!token && !key) || !fileId) return null;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        else if (key) headers['key'] = key;
        const response = await fetch(`${api_path}/file/${encodeURIComponent(fileId)}/visibility`, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(sharing)
        });
        return await parseJsonSafe(response);
    } catch (error) {
        console.error('Update file visibility error:', error);
        return null;
    }
}

/** Create a files record for a file that's on disk but untracked, so it gets an owner + sharing. */
export async function trackUntrackedFile(
    name: string
): Promise<{ success: boolean; file?: FileRecord; message?: string } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';
        if (!api_path || (!token && !key) || !name) return null;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        else if (key) headers['key'] = key;
        const response = await fetch(`${api_path}/file/track`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ name })
        });
        return await parseJsonSafe(response);
    } catch (error) {
        console.error('Track file error:', error);
        return null;
    }
}

export async function listFiles(): Promise<{ success: boolean; files?: FileRecord[] } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';

        if (!api_path || (!token && !key)) return null;

        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        else if (key) headers['key'] = key;

        const response = await fetch(`${api_path}/file/list`, {
            method: 'GET',
            headers
        });

        const data = await response.json();
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        return data;
    } catch (error) {
        console.error('List files error:', error);
        return null;
    }
}

export interface ImageRecord {
    name: string;
    size: number;
    modified?: string;
}

export async function listImages(): Promise<{ success: boolean; images?: ImageRecord[] } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';

        if (!api_path || (!token && !key)) return null;

        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        else if (key) headers['key'] = key;

        const response = await fetch(`${api_path}/file/images`, {
            method: 'GET',
            headers
        });

        const data = await response.json();
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        return data;
    } catch (error) {
        console.error('List images error:', error);
        return null;
    }
}

export interface FinalizeFileParams {
    filename: string;
    collection: string;
    uid: string;
    field: string;
    userId?: string;
}

export async function finalizeFile(params: FinalizeFileParams): Promise<{ success: boolean; filename?: string; message?: string } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';
        if (!api_path || (!token && !key)) return null;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : key ? { key } : {})
        };
        const response = await fetch(`${api_path}/file/finalize`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                filename: params.filename,
                collection: params.collection,
                uid: params.uid,
                field: params.field,
                userId: params.userId
            })
        });
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        return await parseJsonSafe(response);
    } catch (error) {
        console.error('Finalize file error:', error);
        return null;
    }
}

export async function discardTempFile(filename: string): Promise<{ success: boolean; message?: string } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';
        if (!api_path || (!token && !key)) return null;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : key ? { key } : {})
        };
        const response = await fetch(`${api_path}/file/discard-temp`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ filename })
        });
        const data = await response.json();
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        return data;
    } catch (error) {
        console.error('Discard temp file error:', error);
        return null;
    }
}

export async function addFileLink(params: FileLinkParams): Promise<{ success: boolean; message?: string; fileId?: string } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';

        if (!api_path || (!token && !key)) return null;

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : key ? { key } : {})
        };

        const response = await fetch(`${api_path}/file/add-link`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                collection: params.collection,
                itemUid: params.itemUid,
                field: params.field,
                filename: params.filename
            })
        });

        const data = await response.json();
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        return data;
    } catch (error) {
        console.error('Add file link error:', error);
        return null;
    }
}

export async function removeFileLink(params: RemoveLinkParams): Promise<{ success: boolean; message?: string } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';

        if (!api_path || (!token && !key)) return null;

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : key ? { key } : {})
        };

        const response = await fetch(`${api_path}/file/remove-link`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                collection: params.collection,
                itemUid: params.itemUid,
                field: params.field,
                filename: params.filename
            })
        });

        const data = await response.json();
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        return data;
    } catch (error) {
        console.error('Remove file link error:', error);
        return null;
    }
}

export interface FileUsageRelation {
    table: string;
    uid: string;
    field: string;
}

export interface TrackedFileUsage {
    uid: string;
    name: string;
    original: string;
    size: number;
    type: string;
    user: string | null;
    relations: FileUsageRelation[];
    status: 'in-use' | 'orphaned';
    updatedAt?: string | null;
}

export interface UntrackedFileOnDisk {
    name: string;
    status: 'untracked';
}

export interface FileUsageReport {
    success: boolean;
    summary: {
        tracked: number;
        inUse: number;
        orphaned: number;
        onDisk: number;
        untrackedOnDisk: number;
    };
    trackedFiles: TrackedFileUsage[];
    untrackedFiles: UntrackedFileOnDisk[];
    message?: string;
}

export async function fetchFileUsage(): Promise<FileUsageReport | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';

        if (!api_path || (!token && !key)) return null;

        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        else if (key) headers['key'] = key;

        const response = await fetch(`${api_path}/file/usage`, {
            method: 'GET',
            headers
        });

        const data = await response.json();
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        if (!response.ok) {
            return {
                success: false,
                summary: { tracked: 0, inUse: 0, orphaned: 0, onDisk: 0, untrackedOnDisk: 0 },
                trackedFiles: [],
                untrackedFiles: [],
                message: data?.message || `Failed to load usage report (${response.status})`
            };
        }
        return data as FileUsageReport;
    } catch (error) {
        console.error('File usage report error:', error);
        return null;
    }
}

export async function deleteFileById(fileId: string): Promise<{ success: boolean; message?: string } | null> {
    try {
        const api_path = getApiBaseUrl();
        const token = getAuthToken();
        const key = import.meta.env.VITE_LABWICK_TOKEN || '';

        if (!api_path || (!token && !key)) return null;

        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        else if (key) headers['key'] = key;

        const response = await fetch(`${api_path}/file/${fileId}`, {
            method: 'DELETE',
            headers
        });

        const data = await response.json();
        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('authToken');
        }
        return data;
    } catch (error) {
        console.error('Delete file error:', error);
        return null;
    }
}

export default uploadFile;